import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Privacy: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Determine back navigation based on referrer or current user status
  const getBackNavigation = () => {
    const from = searchParams.get('from');
    
    if (from === 'landing') {
      return { to: '/', text: 'Back to landing' };
    }
    
    if (currentUser) {
      return { to: '/dashboard', text: 'Back to dashboard' };
    }
    
    return { to: '/signup', text: 'Back to signup' };
  };
  
  const backNav = getBackNavigation();
  
  return (
    <div className="min-h-screen px-6 sm:px-8 py-12 relative">
      {/* Subtle animated background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-25 pointer-events-none" />
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Back button */}
        <Link 
          to={backNav.to}
          className="inline-flex items-center text-sm text-primary-600 hover:text-black transition-colors mb-8"
        >
          <ArrowLeft size={16} className="mr-2" />
          {backNav.text}
        </Link>

        {/* Header */}
        <div className="animate-fade-up mb-12">
          <h1 className="text-5xl font-display font-bold tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-primary-600">
            Last updated: December 2024
          </p>
        </div>

        {/* Content */}
        <div className="animate-fade-up prose prose-lg max-w-none" style={{ animationDelay: '0.1s' }}>
          <div className="bg-white rounded-2xl p-8 shadow-minimal space-y-8">
            
            <section>
              <h2 className="text-2xl font-display font-bold mb-4">1. Introduction</h2>
              <p className="text-primary-700 leading-relaxed">
                At ClipThis, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered video clipping service. Please read this privacy policy carefully.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold mb-3 text-primary-800">Personal Information</h3>
              <p className="text-primary-700 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700 mb-6">
                <li>Register for an account</li>
                <li>Use our services</li>
                <li>Contact us for support</li>
                <li>Subscribe to our newsletter</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 text-primary-800">Content Data</h3>
              <p className="text-primary-700 leading-relaxed mb-4">
                When you upload videos to our service, we collect and process:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700 mb-6">
                <li>Video files and associated metadata</li>
                <li>Audio tracks for analysis</li>
                <li>Visual content for highlight detection</li>
                <li>Generated clips and analysis results</li>
              </ul>

              <h3 className="text-lg font-semibold mb-3 text-primary-800">Usage Information</h3>
              <p className="text-primary-700 leading-relaxed mb-4">
                We automatically collect certain information about your device and usage:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>IP address and browser information</li>
                <li>Pages visited and features used</li>
                <li>Time and duration of visits</li>
                <li>Error logs and performance data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">3. How We Use Your Information</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Provide, operate, and maintain our services</li>
                <li>Process your videos using AI analysis</li>
                <li>Generate clips and highlights from your content</li>
                <li>Improve and develop our AI algorithms</li>
                <li>Communicate with you about your account and our services</li>
                <li>Provide customer support</li>
                <li>Monitor usage and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">4. AI Processing and Machine Learning</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                Our AI systems analyze your video content to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700 mb-4">
                <li>Detect audio spikes and emotional reactions</li>
                <li>Identify facial expressions and emotions</li>
                <li>Recognize highlight-worthy moments</li>
                <li>Generate timestamp recommendations</li>
              </ul>
              <p className="text-primary-700 leading-relaxed">
                We do not use your content to train our AI models without your explicit consent. All processing is done to provide you with the requested clipping service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li><strong>Service Providers:</strong> Trusted third parties who assist in operating our service</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or transfer of assets</li>
                <li><strong>Consent:</strong> When you have given us explicit permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">6. Data Security</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                We implement appropriate security measures to protect your information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and assessments</li>
                <li>Access controls and authentication requirements</li>
                <li>Secure cloud infrastructure with leading providers</li>
                <li>Regular backups and disaster recovery procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">7. Data Retention</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                We retain your information for different periods depending on the type:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li><strong>Account Information:</strong> Until you delete your account</li>
                <li><strong>Video Content:</strong> For the duration of your subscription plus 30 days</li>
                <li><strong>Generated Clips:</strong> Until manually deleted by you</li>
                <li><strong>Usage Logs:</strong> Up to 2 years for security and improvement purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">8. Your Rights and Choices</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li><strong>Access:</strong> Request copies of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Objection:</strong> Object to certain processing of your information</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">9. Cookies and Tracking</h2>
              <p className="text-primary-700 leading-relaxed mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-primary-700">
                <li>Remember your preferences and settings</li>
                <li>Keep you signed in</li>
                <li>Analyze usage patterns and improve our service</li>
                <li>Provide personalized content and features</li>
              </ul>
              <p className="text-primary-700 leading-relaxed mt-4">
                You can control cookies through your browser settings, but some features may not work properly if cookies are disabled.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">10. International Data Transfers</h2>
              <p className="text-primary-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">11. Children's Privacy</h2>
              <p className="text-primary-700 leading-relaxed">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-primary-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-display font-bold mb-4">13. Contact Us</h2>
              <p className="text-primary-700 leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-primary-50 rounded-lg">
                <p className="text-primary-700">
                  <a href="https://twitter.com/A5omic" target="_blank" rel="noopener noreferrer" className="text-black hover:underline">
                    DM us on Twitter @A5omic
                  </a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 